import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faSpinner, faCircle } from '@fortawesome/free-solid-svg-icons';
import { marketDataApi } from '../api/marketDataApi';

const MarketClock = () => {
  const [clock, setClock] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    loadClock();
    // Refresh clock every 30 seconds
    const interval = setInterval(loadClock, 30000);
    // Update current time every second
    const timeInterval = setInterval(() => setCurrentTime(new Date()), 1000);
    
    return () => {
      clearInterval(interval);
      clearInterval(timeInterval);
    };
  }, []);

  const loadClock = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await marketDataApi.getMarketClock();
      setClock(response.data);
    } catch (err) {
      console.error('Error loading market clock:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load market clock');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const getTimeUntil = (targetDate) => {
    if (!targetDate) return null;
    const target = new Date(targetDate);
    const now = currentTime;
    const diff = target - now;
    
    if (diff <= 0) return 'Now';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-3 border border-gray-100">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-base font-bold text-gray-800 flex items-center">
          <FontAwesomeIcon icon={faClock} className="mr-2 text-indigo-600 text-sm" />
          Market Status
        </h2>
        {loading && (
          <FontAwesomeIcon icon={faSpinner} className="animate-spin text-gray-400 text-xs" />
        )}
      </div>

      {error ? (
        <div className="text-xs text-red-600 py-2">{error}</div>
      ) : clock ? (
        <div className="space-y-2">
          {/* Market Status */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-600">Status:</span>
            <div className="flex items-center space-x-1.5">
              <FontAwesomeIcon 
                icon={faCircle} 
                className={`text-[8px] ${clock.isOpen ? 'text-green-500' : 'text-red-500'}`} 
              />
              <span className={`text-xs font-semibold ${clock.isOpen ? 'text-green-600' : 'text-red-600'}`}>
                {clock.isOpen ? 'OPEN' : 'CLOSED'}
              </span>
            </div>
          </div>

          {/* Current Time */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-600">Current Time:</span>
            <span className="text-xs font-medium text-gray-800">
              {currentTime.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: true,
              })}
            </span>
          </div>

          {/* Next Open/Close */}
          {clock.isOpen ? (
            <div className="pt-1 border-t border-gray-200">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-600">Next Close:</span>
                <span className="text-xs font-medium text-gray-800">
                  {formatTime(clock.nextClose)}
                </span>
              </div>
              <div className="text-[10px] text-gray-500 text-right">
                {getTimeUntil(clock.nextClose)}
              </div>
            </div>
          ) : (
            <div className="pt-1 border-t border-gray-200">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-600">Next Open:</span>
                <span className="text-xs font-medium text-gray-800">
                  {formatTime(clock.nextOpen)}
                </span>
              </div>
              <div className="text-[10px] text-gray-500 text-right">
                {getTimeUntil(clock.nextOpen)}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-2 text-gray-500 text-xs">
          Loading market status...
        </div>
      )}
    </div>
  );
};

export default MarketClock;


